import React, { useEffect, useMemo, useState, useContext } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import * as XLSX from "xlsx";
import axios from "../../api/axios";
import TableSortLabel from "@mui/material/TableSortLabel";

import PageButtonWhite from "../GeneralComponents/PageButtonWhite";
import PageButtonSimple from "../GeneralComponents/PageButtonSimple";
import { Download as DownloadIcon } from "@mui/icons-material";

import InputFilter from "../GeneralComponents/InputFilter";
import InputFile from "../GeneralComponents/InputFile";

import GeneralContext from '../GeneralContext/GeneralContext';
import SnackbarContext from "../GeneralContext/SnackbarContext";
import { PERIODS, ROL_OP_ADMIN, ROL_OP_INS } from "../../Constants/Constants";
import PageButtonBlack from "../GeneralComponents/PageButtonBlack";


const filenameFromUrlNoExt = (url) => {
  if (!url) return "";
  try {
    const clean = String(url).split("?")[0]; // por si viene con query
    const last = clean.substring(clean.lastIndexOf("/") + 1);
    return last.replace(/\.[^/.]+$/, ""); // quita extensión
  } catch {
    return "";
  }
};

const currentMonthYYYYMM01 = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
};

const getPeriodoActual = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}-01`;
};

const sortPeriodsDesc = (periods = []) =>
  [...periods]
    .filter(Boolean)
    .sort((a, b) => b.localeCompare(a));

const buildApiWithParams = (baseApi, paramsObj) => {
  if (!baseApi) return baseApi;

  const params = new URLSearchParams();
  Object.entries(paramsObj || {}).forEach(([k, v]) => {
    if (v != null && String(v).length > 0) params.set(k, String(v));
  });

  const qs = params.toString();
  if (!qs) return baseApi;

  return baseApi.includes("?") ? `${baseApi}&${qs}` : `${baseApi}?${qs}`;
};

const parseNumber = (v) => {
  if (v == null) return null;
  const s = String(v).trim().replace(/\./g, "").replace(",", ".");
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const parseDate = (v) => {
  if (!v) return null;
  const s = String(v).trim();
  const d = new Date(s.replace(" ", "T"));
  return isNaN(d.getTime()) ? null : d.getTime();
};

const compareValues = (a, b, type = "string") => {
  if (a == null || a === "") return b == null || b === "" ? 0 : 1;
  if (b == null || b === "") return -1;

  if (type === "number") {
    const na = parseNumber(a);
    const nb = parseNumber(b);
    if (na == null) return nb == null ? 0 : 1;
    if (nb == null) return -1;
    return na - nb;
  }

  if (type === "date") {
    const da = parseDate(a);
    const db = parseDate(b);
    if (da == null) return db == null ? 0 : 1;
    if (db == null) return -1;
    return da - db;
  }

  return String(a).localeCompare(String(b), "es", { sensitivity: "base" });
};

const fmtCL = new Intl.NumberFormat("es-CL");

const formatThousands = (v) => {
  const n = typeof v === "number" ? v : parseNumber(v);
  return n == null ? "" : fmtCL.format(n);
};


export default function GenericTextList({
  title = "Listado",
  columns = [],
  filtersConfig = [],
  dataSource,
  api,
  selectable = true,
  breadcrumb,
  periodoMode = "api",
}) {


  const openSnackbar = useContext(SnackbarContext);
  const { userSesion, reloadChildren, includeRol } = useContext(GeneralContext);

  const [orderBy, setOrderBy] = useState(null); 
const [order, setOrder] = useState("asc");
  const [rows, setRows] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingFile, setLoadingFile] = useState(false);

  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(0);
  const [rpp, setRpp] = useState(10);
  const [selected, setSelected] = useState([]);

  const [operadores, setOperadores] = useState([]);
const [operadorNames, setOperadorNames] = useState([]);
const [operadorNameToId, setOperadorNameToId] = useState({});

const isOpRol = includeRol?.([ROL_OP_ADMIN, ROL_OP_INS]);
const restrictToUserOperador = isOpRol && !!userSesion?.id_operador;

  const periodoFilterCfg = useMemo(
    () => filtersConfig.find((f) => f?.item === "periodo"),
    [filtersConfig]
  );

  const operadoresCfg = useMemo(
  () => filtersConfig.find((f) => f?.source === "operadores"),
  [filtersConfig]
);

  const monthNow01 = useMemo(() => currentMonthYYYYMM01(), []);
const defaultPeriod = useMemo(() => {
  const periodsDesc = sortPeriodsDesc(PERIODS);

  if (periodsDesc.includes(monthNow01)) {
    return monthNow01;
  }
  return periodsDesc[0] ?? monthNow01;
}, [monthNow01]);

  const [selectedPeriod, setSelectedPeriod] = useState(() => PERIODS[0]);


useEffect(() => {
  if (periodoMode !== "api") return;

  if (selectedPeriod && String(selectedPeriod).length > 0) return;

  if (defaultPeriod) setSelectedPeriod(defaultPeriod);
}, [defaultPeriod, periodoMode, selectedPeriod]);


const applyFilters = (nextFilters, baseRows = rows, nextRpp = rpp) => {
  const activeEntries = Object.entries(nextFilters).filter(
    ([, val]) => val != null && String(val).length > 0
  );

  const filtered =
    activeEntries.length === 0
      ? baseRows
      : baseRows.filter((row) =>
          activeEntries.every(([key, val]) => {

            return String(row[key] ?? "") === String(val);
          })
        );

  setOrderBy(null);
  setOrder("asc");

  setTableData(filtered);
  setSelected([]);

  const maxPage = Math.floor(Math.max(filtered.length - 1, 0) / nextRpp);
  if (page > maxPage) setPage(0);
};


const handleFilter = (item, value) => {
  console.log()
  if (item === "periodo" && periodoMode === "api") {
    const v = value;
    if (v) setSelectedPeriod(v);
    return;
  }

  const nf = { ...filters, [item]: value };


  if (value == null || String(value).length === 0) {
    delete nf[item];
  }

  setFilters(nf);
  applyFilters(nf);
};



  useEffect(() => {
  let alive = true;

  const loadOps = async () => {
    if (!operadoresCfg) return;
    try {
      const resp = await axios.getOperadores();
      const list = resp?.data || [];
      if (!alive) return;

      setOperadores(list);

      const names = list
        .map((o) => String(o?.nombre_operador ?? ""))
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));

      const map = {};
      list.forEach((o) => {
        const n = String(o?.nombre_operador ?? "");
        if (n) map[n] = o?.id_operador;
      });

      setOperadorNames(names);
      setOperadorNameToId(map);
    } catch (e) {
      console.error("Error cargando operadores:", e);
      if (alive) {
        setOperadores([]);
        setOperadorNames([]);
        setOperadorNameToId({});
      }
    }
  };

  loadOps();
  return () => {
    alive = false;
  };
}, [operadoresCfg]);



  useEffect(() => {
  if (!operadoresCfg) return;
  if (!operadorNames.length) return;

  const def = operadoresCfg?.defaultValue;
  if (!def) return;

  setFilters((prev) => {
    if (prev?.[operadoresCfg.item]) return prev;
    const nf = { ...(prev || {}), [operadoresCfg.item]: def };
    applyFilters(nf);
    return nf;
  });
}, [operadorNames, operadoresCfg]);


const buildApiUrl = useMemo(() => {
  if (!api || typeof api !== "string") return api;

  const params = {};

  if (periodoFilterCfg && periodoMode === "api") {
    params.periodo = selectedPeriod+"-01";
  }

  for (const f of filtersConfig) {
    if (!f?.queryParam) continue;

    const val = filters?.[f.item];

    if (f.source === "operadores") {
      const id = operadorNameToId?.[String(val ?? "")];
      if (id != null && String(id).length > 0) {
        params[f.queryParam] = id;
      }
      continue;
    }

    if (val != null && String(val).length > 0) {
      params[f.queryParam] = val;
    }
  }
   if (restrictToUserOperador) {
    params.id_operador = String(userSesion.id_operador);
  }

  return buildApiWithParams(api, params);
}, [api, filtersConfig, filters, periodoFilterCfg, selectedPeriod, operadorNameToId, periodoMode]);


const loadKey = useMemo(() => {
  if (typeof dataSource === "function") return "datasource";
  if (typeof api !== "string" || !api) return "noapi";

  if (periodoMode !== "api") return api;

  return buildApiUrl;
}, [dataSource, api, buildApiUrl, periodoMode]);

useEffect(() => {
  let alive = true;

  const load = async () => {
    try {
      setLoading(true);
      let data = [];
      if (typeof dataSource === "function") {
        data = await dataSource();
      } else if (typeof api === "string" && api.length > 0) {
        const urlToFetch =
        restrictToUserOperador ? buildApiUrl :
        (periodoMode === "api" ? buildApiUrl : api);
        const resp = await axios.getReport(urlToFetch);
        data = resp.data || [];
      }

      if (!alive) return;

      const normalized = data.map((r, idx) => ({
        id: r.id ?? idx,
        index: idx,
        ...r,
        ...(title === "OTROS REPORTES" && { nombre_archivo: filenameFromUrlNoExt(r.url),}),
        ...(Object.prototype.hasOwnProperty.call(r, "is_recibido") && {
          is_recibido: r.is_recibido === 1 ? "SI" : "NO",
        }),
        ...(Object.prototype.hasOwnProperty.call(r, "recibido_en") && {
          recibido_en: r.recibido_en?.replace("T", " ").slice(0, 16),
        }),
      }));

      applyFilters(filters, normalized); 
      setRows(normalized);
      setTableData(normalized);
      setSelected([]);
      setPage(0);


    } catch (e) {
      if (alive) {
        console.error("Error cargando datos:", e);
        setRows([]);
        setTableData([]);
        setSelected([]);
        setPage(0);
      }
    } finally {
      if (alive) setLoading(false);
    }
  };

  load();
  return () => {
    alive = false;
  };
}, [loadKey, periodoMode, buildApiUrl, api, dataSource, operadoresCfg]);





const handleSelectAll = (e) => {
  if (!selectable) return;

  setSelected(e.target.checked ? visibleRows.map((r) => r.index) : []);
};

  const handleCheckboxClick = (idx) => {
    if (!selectable) return;
    setSelected((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  };

  const downloadXLS = () => {
    const selectedRows = tableData.filter((row) => selected.includes(row.index));
    const prepared = selectedRows.map((row) => {
      const obj = {};
      columns.forEach((c) => {
        obj[c.label] = row[c.id] ?? "";
      });
      return obj;
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(prepared);
    XLSX.utils.book_append_sheet(wb, ws, "Tabla");
    XLSX.writeFile(wb, (title || "tabla") + ".xlsx");
  };

    const downloadXLSAll = () => {
    const selectedRows = tableData;
    const prepared = selectedRows.map((row) => {
      const obj = {};
      columns.forEach((c) => {
        obj[c.label] = row[c.id] ?? "";
      });
      return obj;
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(prepared);
    XLSX.utils.book_append_sheet(wb, ws, "Tabla");
    XLSX.writeFile(wb, (title || "tabla") + ".xlsx");
  };

  const [evError, setEvError] = useState(false);
  const [reportFile, setReportFile] = useState();
  const [downloadLoader, setDownloadLoader] = useState(false);

  const downloadPDF = (url) => {
    window.open(url, "_blank");
  };

  const setFile = (archivo) => {
    setEvError(!archivo ? true : false);
    setReportFile((prev) => ({
      ...(prev || {}),
      archivo,
    }));
  };

  const sortedTableData = useMemo(() => {
  if (!orderBy) return tableData;

  const colCfg = columns.find((c) => c.id === orderBy);
  const type = colCfg?.sortType || "string";

  const dir = order === "asc" ? 1 : -1;

  return [...tableData].sort((r1, r2) => {
    const res = compareValues(r1[orderBy], r2[orderBy], type);
    if (res !== 0) return res * dir;
    return (r1.index ?? 0) - (r2.index ?? 0);
  });
}, [tableData, orderBy, order, columns]);

  const visibleRows = useMemo(
  () => sortedTableData.slice(page * rpp, page * rpp + rpp),
  [sortedTableData, page, rpp]
);

  const handleUploadDocumentoOperador = () => {
  setLoadingFile(true);

  if (!reportFile) {
    setLoadingFile(false);
    openSnackbar("Debes seleccionar un PDF", "error");
    return;
  }

  const periodo = getPeriodoActual();

  const form = new FormData();

  form.append("periodo", periodo);
  form.append("id_operador", String(userSesion?.id_operador));
  form.append("nombre_operador", userSesion?.nombre_operador);

  form.append("file", reportFile.archivo, reportFile.archivo.name || "reporte.pdf");


  axios
    .postDocumentosOperador(form)
    .then(() => {
      setLoadingFile(false);
      openSnackbar("Operación exitosa", "success");
      reloadChildren();
    })
    .catch((error) => {
      setLoadingFile(false);
      openSnackbar("Error al subir el documento", "error");
      console.error(error);
    });
};

const downloadZip = async () => {
  try {
    if (selected.length === 0) return;

    const ids = selected
      .map((idx) => tableData.find((r) => r.index === idx)?.id)
      .filter((id) => id != null && String(id).length > 0);

    if (ids.length !== selected.length) {
      openSnackbar("Hay elementos seleccionados sin ID para descargar.", "error");
      return;
    }

    setDownloadLoader(true);

    const now = new Date();
    const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}_${String(now.getHours()).padStart(2, "0")}-${String(now.getMinutes()).padStart(2, "0")}-${String(now.getSeconds()).padStart(2, "0")}`;

    const response = await axios.downloadReports(ids);

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `reportes_${timestamp}.zip`);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  } catch (error) {
    console.error("Error al descargar reportes:", error);
    openSnackbar("Error al descargar reportes.", "error");
  } finally {
    setDownloadLoader(false);
  }
};


  return (
    <>
      {filtersConfig.length > 0 && (
        <Grid container spacing={0} sx={{ m: 0, p: 0 }} alignItems="flex-start">
          <Grid size={12}>
            <Grid container spacing={1} sx={{ mb: 2 }}>
              {filtersConfig
  .filter((f) => {
    if (!restrictToUserOperador) return true;

    return f.item !== "nombre_operador" && f.source !== "operadores";
  })
  .map((f) => {
                const { item, name, size = 6, opt, defaultOpt, source } = f;

 
                return (
                  <Grid key={item} size={size}>
                    <InputFilter
                      data={item === "nombre_operador" ? rows : (item === "periodo" ? rows : visibleRows)}
                      item={item}
                      name={name}
                      callback={handleFilter}
                      opt={source === "operadores" ? operadorNames : (item === "periodo" ? PERIODS : opt)}
                      defaultOpt={defaultOpt}
                      value={
                        item === "periodo"
                          ? (periodoMode === "api" ? selectedPeriod : (filters?.periodo ?? null))
                          : (filters?.[item] ?? null)}
                    />

                  </Grid>
                );
              })}
            </Grid>
          </Grid>
        </Grid>
      )}

      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="40vh"
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          {title === "OTROS REPORTES" && userSesion?.id_operador ? (
            <Paper sx={{ mb: 2, p: 1.25, textAlign: "center" }}>
              <Box sx={{ p: "2rem 9rem", textAlign: "center" }}>
                {breadcrumb ? (
                  <Box sx={{ display: "inline-block", textAlign: "center" }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        textAlign: "center",
                        fontSize: "1.08rem",
                        lineHeight: 1.2,
                      }}
                    >
                      {breadcrumb}
                    </Typography>
                    <Box
                      sx={{
                        width: "100%",
                        borderBottom: "1px solid #111",
                        mt: 0.75,
                      }}
                    />
                  </Box>
                ) : null}

                <Stack alignItems="left" justifyContent="center" spacing={2}>
                  <Typography
                    variant="h1"
                    color="inherit"
                    noWrap
                    sx={{ flexGrow: 1, textAlign: "center", fontWeight: 700 }}
                  >
                    OTROS REPORTES
                  </Typography>

                  <Box
                    justifyContent="center"
                    sx={{
                      maxWidth: "100%",
                      margin: "0 auto",
                      display: "flex",
                    }}
                  >
                    <Typography
                      variant="body"
                      align="center"
                      sx={{ marginBottom: "2rem" }}
                    >
                      En el siguiente campo cargue el archivos con la información
                      que necesite reportar para dejar en los registros.
                    </Typography>
                  </Box>
                </Stack>

                <Grid container spacing={2}>
                  <Grid size={12}>
                    <Box>
                      <InputFile
                        onFileSelect={setFile}
                        fileType="pdf"
                        placeholder="Archivo"
                        buttonText="Subir archivo"
                        sizeBtnGrid={4}
                      />
                      <Typography
                        variant="small"
                        align="left"
                        sx={{
                          marginBottom: "18px",
                          color: evError ? "red" : "#111",
                        }}
                      >
                        {evError ? "Debe adjuntar un archivo" : ""}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={12} sx={{pt: 2}}>
                    <PageButtonBlack loading={loadingFile || !userSesion?.id_operador || !reportFile || evError} onClick={() => {handleUploadDocumentoOperador()}}>
              Subir reporte
            </PageButtonBlack>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          ) : null}

          <Paper sx={{ mb: 2, p: 1.25, textAlign: "center" }}>
            {breadcrumb && title !== "OTROS REPORTES" ? (
              <Box sx={{ display: "inline-block", textAlign: "center", mt: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    textAlign: "center",
                    fontSize: "1.08rem",
                    lineHeight: 1.2,
                  }}
                >
                  {breadcrumb}
                </Typography>
                <Box
                  sx={{
                    width: "100%",
                    borderBottom: "1px solid #111",
                    mt: 0.75,
                  }}
                />
              </Box>
            ) : null}

            <Typography
              variant="h1"
              color="inherit"
              sx={{ textAlign: "center", p: "0.5rem" }}
            >
              {title} {title === "OTROS REPORTES" ? "ANTERIORES" : ""}
            </Typography>
          </Paper>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {selectable && (
                    <TableCell align="left" sx={{ width: 48 }}>
                      <Checkbox
                          indeterminate={
                            visibleRows.length > 0 &&
                            visibleRows.some((r) => selected.includes(r.index)) &&
                            !visibleRows.every((r) => selected.includes(r.index))
                          }
                          checked={
                            visibleRows.length > 0 &&
                            visibleRows.every((r) => selected.includes(r.index))
                          }
                          onChange={handleSelectAll}
                        />
                    </TableCell>
                  )}

                  {columns.map((col) => {
                        const sortable = !!col.sortable;

                        return (
                          <TableCell
                            key={col.id}
                            align={col.align || "left"}
                            sx={{ minWidth: col.minWidth, ...(col.maxWidth && { maxWidth: col.maxWidth }) }}
                            sortDirection={orderBy === col.id ? order : false}
                          >
                            {sortable ? (
                              <TableSortLabel
                                active={orderBy === col.id}
                                direction={orderBy === col.id ? order : "asc"}
                                onClick={() => {
                                  setPage(0); // opcional: vuelve a primera página
                                  if (orderBy !== col.id) {
                                    setOrderBy(col.id);
                                    setOrder("asc");
                                  } else {
                                    setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
                                  }
                                }}
                              >
                                {col.label}
                              </TableSortLabel>
                            ) : (
                              col.label
                            )}
                          </TableCell>
                        );
                      })}

                </TableRow>
              </TableHead>

              <TableBody>
                {visibleRows.map((row) => (
                  <TableRow
                    key={row.id}
                    hover={!!selectable}
                    selected={selected.includes(row.index)}
                  >
                    {selectable && (
                      <TableCell>
                        <Checkbox
                          checked={selected.includes(row.index)}
                          onClick={() => handleCheckboxClick(row.index)}
                        />
                      </TableCell>
                    )}
                    {columns.map((col) => (
                      <TableCell key={col.id} align={col.align || "left"}>
                        {col.id?.startsWith("url") ? (
                          row[col.id] ? (
                            <PageButtonSimple
                              width="140px"
                              pad="0.2rem 0.5rem"
                              fs="0.6875rem"
                              startIcon={<DownloadIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                downloadPDF(row[col.id]);
                              }}
                            >
                              <Stack>
                                <span>Descargar {col.id === "url" ? "" : "prefactura"}</span>
                              </Stack>
                            </PageButtonSimple>
                          ) : (
                            <b>-</b>
                          )
                        ) : col.sortType === "number" && col.id !== "rbd" ? (
                          formatThousands(row[col.id])
                        ) : (
                          row[col.id] ?? ""
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {tableData.length === 0 && (
              <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
                Sin datos
              </Box>
            )}

            <TablePagination
              component="div"
              rowsPerPageOptions={[10, 25, 100]}
              count={tableData.length}
              rowsPerPage={rpp}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={(e) => {
                setRpp(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </TableContainer>

          {selectable && (
          <Grid container spacing={2} justifyContent="flex-end" sx={{ pt: 2 }}>
            {title === "OTROS REPORTES" ? (
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <PageButtonWhite
                  loading={downloadLoader || selected.length === 0}
                  onClick={downloadZip}
                  width="100%"
                >
                  Descarga selección de documentos (.zip)
                </PageButtonWhite>
              </Grid>
            ) : (
              <>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <PageButtonWhite onClick={downloadXLSAll} width="100%">
                    Descargar todo (.xls)
                  </PageButtonWhite>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <PageButtonWhite
                    onClick={downloadXLS}
                    width="100%"
                    disabled={selected.length === 0}
                  >
                    Descargar selección (.xls)
                  </PageButtonWhite>
                </Grid>
              </>
            )}
          </Grid>
        )}
        </>
      )}
    </>
  );
}
